// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

// D-COST-6 — Profitability "v2 (beta)". Runs next to the Current tab until v2 reaches parity.
// Model (owner, 2026-10-12): Net profit = Revenue − S·M·L·E reserve − every expense (whole Azure RG +
// other costs), then split Blue Dev / Owl. Layout: month + headline numbers on top, a left rail of
// sections, ONE section on screen at a time. "Monthly P&L" is the home: every month's expenses, net
// profit and each share in one table. Section and month live in the URL so a refresh keeps your place.

import { useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import Box from '@mui/material/Box'
import { ChevronLeft, ChevronRight, Cloud, DatabaseZap, GitBranch, HandCoins, Layers, ListTree, Lock, ReceiptText, RefreshCw, Settings2, Users } from 'lucide-react'
import ErrorBoundary from '../../../components/ui/ErrorBoundary'
import LoadState from '../../../components/ui/LoadState'
import Notice from '../../../components/ui/Notice'
import IconAction from '../../../components/ui/IconAction'
import Section, { StatTile } from '../../../components/ui/Section'
import SideTabs from '../../../components/ui/SideTabs'
import { financeKeys, getFinanceSummary, type FinanceSummary } from '../../../api/finance'
import { CustomerTable, ExpensesBlock, MonthlyPnlTable, PnlStatement, ResourceExplorer, money } from './FinanceTables'
import OtherCostsPanel from './OtherCostsPanel'
import GithubPanel from './GithubPanel'
import DataSourcesPanel from './DataSourcesPanel'
import CustomerPricingDialog from './CustomerPricingDialog'
import SettingsPanel from './SettingsPanel'
import MonthBreakdown, { projectedNet } from './MonthBreakdown'
import { monthLabel } from './CostItemDialogs'

const SECTIONS = ['pnl', 'month', 'income', 'expenses', 'github', 'customers', 'resources', 'sources', 'settings'] as const
type SectionKey = typeof SECTIONS[number]

function Kpis({ data }: { data: FinanceSummary }) {
  const p = data.detail?.pnl
  const dev = data.settings.dev_split_pct
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, mb: 2 }}>
      <StatTile label="Revenue" value={money(p?.revenue_usd ?? data.mrr_usd)}
        hint={p && (p.other_income_usd || p.credits_usd) ? `licenses ${money(p.license_usd)} + other ${money(p.other_income_usd - p.credits_usd)}` : `ARR ${money(data.mrr_usd * 12)}`} />
      <StatTile label="Total expenses" value={p ? money(data.detail?.expenses.total_usd) : '—'} hint={p ? `+ ${money(p.reserve_usd)} reserve` : undefined} />
      <StatTile label="Net profit" value={p ? money(p.net_profit_usd) : '—'} hint={p ? `${p.net_margin_pct}% of revenue` : undefined} />
      <StatTile label={`Blue Dev ${p?.dev_split_pct ?? dev}%`} value={p ? money(p.blue_dev_usd) : '—'} color="#6d28d9" />
      <StatTile label={`Owl ${100 - (p?.dev_split_pct ?? dev)}%`} value={p ? money(p.owl_usd) : '—'} color="#0f766e" />
    </Box>
  )
}

function SectionView({ section, data, openMonth }: { section: SectionKey; data: FinanceSummary; openMonth: (m: string) => void }) {
  const d = data.detail
  const s = data.settings
  const when = data.month === data.current_month ? `${monthLabel(data.month)} (month to date)` : monthLabel(data.month)
  if (section === 'pnl') return (
    <>
      {d && (
        <Section title={`${when} — net profit and shares`}
          subtitle={`Reserve comes off the top, then every expense. What is left is split ${s.dev_split_pct}% Blue Dev / ${100 - s.dev_split_pct}% Owl.`}>
          <PnlStatement pnl={d.pnl} expenses={d.expenses} settings={s} githubAvailable={d.github_available} />
        </Section>
      )}
      <Section title="Every month" subtitle="One row per month: expenses, net profit and each share. “Breakdown →” opens every line of that month.">
        {data.history.length === 0
          ? <Box sx={{ fontSize: 13, color: 'text.secondary' }}>No months recorded yet.</Box>
          : <MonthlyPnlTable rows={data.history} onOpen={openMonth} />}
      </Section>
    </>
  )
  if (section === 'month') return (
    <Section title={`Month breakdown — ${when}`}
      subtitle="Every line of the month, grouped, down to net profit and each share. Click a group to open its lines. Use ◀ ▶ above to change month.">
      <MonthBreakdown data={data} />
    </Section>
  )
  if (section === 'settings') return <SettingsPanel />
  if (section === 'sources') return <DataSourcesPanel history={data.history} currentMonth={data.current_month} autoCloseDay={data.auto_close_day} autoCloseEnabled={data.auto_close_enabled} />
  if (!d) return <Section title="No data"><Box sx={{ fontSize: 13, color: 'text.secondary' }}>No cost snapshot for {monthLabel(data.month)}.</Box></Section>
  if (section === 'expenses') return (
    <Section title={`Expenses — ${when}`}
      subtitle="Every real cost of the month. All of it comes off before the profit is split; none of it is charged to one partner only.">
      <ExpensesBlock expenses={d.expenses} githubAvailable={d.github_available} />
      <OtherCostsPanel month={data.month} lines={d.other_lines} />
    </Section>
  )
  if (section === 'income') return (
    <Section title={`Income & credits — ${when}`}
      subtitle="Money in besides the monthly licenses — setup fees, extra billing — and credits or discounts given back. Revenue = licenses + income − credits; it all counts before the reserve and the split. Tie a line to a customer to see it on that customer’s contribution.">
      <OtherCostsPanel mode="income" month={data.month} lines={d.income_lines}
        customers={d.customers.map(c => ({ id: c.pharmacy_id, name: c.name }))} />
    </Section>
  )
  if (section === 'github') return (
    <Section title={`GitHub — ${when}`} subtitle="Org OwlConsultations. Net cost is what Owl pays after the plan’s included minutes and storage.">
      <GithubPanel detail={d} />
    </Section>
  )
  if (section === 'customers') return <CustomersSection data={data} when={when} />
  return (
    <Section title={`Azure resources — owlrx-mockup-rg · ${money(d.expenses.azure_usd)}`}
      subtitle={d.cost_source === 'azure_export'
        ? 'From the daily cost export: every resource classified once, with what it was billed for. Together they are the Azure expense.'
        : 'From the hourly snapshot (the cost export does not cover this month yet): every resource classified once. Together they are the Azure expense.'}>
      <ResourceExplorer rows={d.resources} meters={d.meters} />
    </Section>
  )
}

/** Customers + the price dialog (change what a customer pays, with its full history). */
function CustomersSection({ data, when }: { data: FinanceSummary; when: string }) {
  const [pricing, setPricing] = useState<string | null>(null)
  const d = data.detail!
  const s = data.settings
  return (
    <Section title={`Customers — ${when}`}
      subtitle={<>What each customer leaves after the {s.reserve_pct}% reserve and its own database, before the shared expenses. The Blue Dev / Owl
        split happens on the whole month, not per customer. <b>Price…</b> changes what a customer pays from a chosen month on; earlier months
        keep their price, and every change is kept in its history.</>}>
      <CustomerTable rows={d.customers} reservePct={s.reserve_pct} onPrice={setPricing} />
      <CustomerPricingDialog pharmacyId={pricing} onClose={() => setPricing(null)} />
    </Section>
  )
}

export default function FinanceV2Tab() {
  const [params, setParams] = useSearchParams()
  const raw = params.get('section')
  const section: SectionKey = (SECTIONS as readonly string[]).includes(raw ?? '') ? raw as SectionKey : 'pnl'
  const month = params.get('month') ?? undefined
  const setParam = (k: string, v: string | undefined) => setParams(prev => {
    const next = new URLSearchParams(prev)
    if (v) next.set(k, v); else next.delete(k)
    return next
  }, { replace: true })

  const q = useQuery({
    queryKey: financeKeys.summary(month),
    queryFn: () => getFinanceSummary(month),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  })

  const months = q.data?.months ?? []               // newest first
  const shown = q.data?.month ?? month
  const i = shown ? months.indexOf(shown) : -1
  const older = i >= 0 && i < months.length - 1 ? months[i + 1] : undefined
  const newer = i > 0 ? months[i - 1] : undefined

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
        <IconAction label="Previous month" icon={<ChevronLeft size={16} />} disabled={!older} onClick={() => setParam('month', older)} />
        <Box sx={{ fontWeight: 700, minWidth: 90, textAlign: 'center' }}>{shown ? monthLabel(shown) : '…'}</Box>
        <IconAction label="Next month" icon={<ChevronRight size={16} />} disabled={!newer} onClick={() => setParam('month', newer)} />
        <IconAction label="Reload" icon={<RefreshCw size={14} />} disabled={q.isFetching} onClick={() => { void q.refetch() }} />
        <Box sx={{ fontSize: 12, color: 'text.secondary', ml: 1 }}>
          {q.data?.close?.status === 'closed'
            ? <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}><Lock size={13} /> <b>Closed</b> — frozen {q.data.close.frozen_at ? new Date(q.data.close.frozen_at).toLocaleString() : ''}</Box>
            : q.data?.cost_as_of
              ? <>Cost as of <b>{new Date(q.data.cost_as_of).toLocaleString()}</b> ({q.data.cost_source === 'azure_export' ? 'daily cost export' : 'hourly snapshot'})</>
              : null}
        </Box>
      </Box>
      <LoadState query={q} what="profitability v2" isEmpty={() => false} empty={null}>
        {data => {
          const d = data.detail
          return (
            <>
              {!data.cost_available && (
                <Notice severity="warning">No cost snapshot for {monthLabel(data.month)} — {data.cost_reason}. Figures show “—”, never $0.00.</Notice>
              )}
              {data.cost_available && data.cost_stale && (
                <Notice severity="warning">Cost figures are older than {data.cost_stale_after_hours}h — last known-good numbers, not current.</Notice>
              )}
              <Kpis data={data} />
              <SideTabs ariaLabel="Profitability sections" value={section}
                onChange={v => setParam('section', v === 'pnl' ? undefined : v)} tabs={[
                  { value: 'pnl', label: 'Monthly P&L', icon: <ReceiptText size={16} />,
                    meta: d ? `Net ${money(d.pnl.net_profit_usd)}` : `${data.history.length} months` },
                  { value: 'month', label: 'Month breakdown', icon: <ListTree size={16} />,
                    meta: d ? (data.month === data.current_month
                      ? `${monthLabel(data.month)} · projected net ${money(projectedNet(data))}`
                      : `${monthLabel(data.month)} · net ${money(d.pnl.net_profit_usd)}`) : undefined },
                  { value: 'income', label: 'Income & credits', icon: <HandCoins size={16} />,
                    meta: d ? `${money(d.pnl.other_income_usd - d.pnl.credits_usd)} · ${d.income_lines.length} line${d.income_lines.length === 1 ? '' : 's'}` : undefined },
                  { value: 'expenses', label: 'Expenses & other costs', icon: <Layers size={16} />,
                    meta: d ? `${money(d.expenses.total_usd)} · ${d.other_lines.length} other` : undefined },
                  { value: 'github', label: 'GitHub', icon: <GitBranch size={16} />,
                    meta: d ? (d.github_available ? money(d.expenses.github_usd) : 'not connected') : undefined },
                  { value: 'customers', label: 'Customers', icon: <Users size={16} />,
                    meta: d ? `${d.pnl.customers_licensed} licensed` : undefined },
                  { value: 'resources', label: 'Azure resources', icon: <Cloud size={16} />,
                    meta: d ? `${d.resources.length} · ${money(d.expenses.azure_usd)}` : undefined },
                  { value: 'sources', label: 'Data sources & close', icon: <DatabaseZap size={16} />,
                    meta: data.cost_source === 'azure_export' ? 'cost export' : 'snapshot (export not yet)' },
                  { value: 'settings', label: 'Settings', icon: <Settings2 size={16} />,
                    meta: `split ${data.settings.dev_split_pct}/${100 - data.settings.dev_split_pct} · auto-close ${data.auto_close_enabled ? 'on' : 'paused'}` },
                ]}>
                {/* One broken section shows an error card with Retry; the rail, the KPIs and the other sections stay usable (E-07). */}
                <ErrorBoundary what="this section" resetKey={`${section}:${data.month}`} onRetry={() => { void q.refetch() }}>
                  <SectionView section={section} data={data} openMonth={m => setParams(prev => {
                    const next = new URLSearchParams(prev); next.set('month', m); next.set('section', 'month'); return next
                  }, { replace: true })} />
                </ErrorBoundary>
              </SideTabs>
            </>
          )
        }}
      </LoadState>
    </Box>
  )
}
